import { useState, useEffect, useMemo } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import { Plus, Trash2, Edit2, Building2, Search, Users, MapPin, ShieldCheck, BadgeCheck, Eye, EyeOff, Monitor } from 'lucide-react'

interface AdminClubsProps {
  onViewClubDashboard?: (clubId: string) => void
}

function AdminClubs({ onViewClubDashboard }: AdminClubsProps) {
  const { clubs, players, addClub, updateClub, bulkUpdatePlayers, deleteClub, fetchClubs } = usePlayerStore()
  const { user } = useAuthStore()
  const [showForm, setShowForm] = useState(false)
  const [editingClubId, setEditingClubId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    competitions: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [bulkUpdatingClubId, setBulkUpdatingClubId] = useState<string | null>(null)

  useEffect(() => {
    fetchClubs()
  }, [fetchClubs])

  const normalizeClubName = (value?: string | null) => value?.trim().toLowerCase().replace(/\s+/g, ' ') || ''

  const playerBelongsToClub = (player: typeof players[number], club: typeof clubs[number]) => {
    const clubOwnerId = (club as any).scout_id as string | undefined
    const clubName = normalizeClubName(club.name)

    if (player.owner_club_id === club.id) return true
    if (clubOwnerId && player.owner_club_id === clubOwnerId) return true
    return normalizeClubName(player.club) === clubName
  }

  const clubCards = useMemo(() => {
    return clubs.map((club) => {
      const squadSize = players.filter((player) => playerBelongsToClub(player, club)).length
      return { ...club, squadSize }
    })
  }, [clubs, players])

  const filteredClubs = useMemo(() => {
    const term = query.toLowerCase()
    return clubCards.filter((club) =>
      club.name.toLowerCase().includes(term) ||
      (club.location || '').toLowerCase().includes(term)
    )
  }, [clubCards, query])

  const stats = useMemo(() => {
    const totalClubs = clubs.length
    const clubsWithSquads = clubCards.filter((club) => club.squadSize > 0).length
    const totalPlayersLinked = clubCards.reduce((sum, club) => sum + club.squadSize, 0)
    return { totalClubs, clubsWithSquads, totalPlayersLinked }
  }, [clubs.length, clubCards])

  const resetForm = () => {
    setFormData({ name: '', location: '', competitions: '' })
    setEditingClubId(null)
    setShowForm(false)
    setError('')
  }

  const openCreate = () => {
    setEditingClubId(null)
    setFormData({ name: '', location: '', competitions: '' })
    setError('')
    setShowForm(true)
  }

  const openEdit = (club: { id: string; name: string; location?: string | null; competitions?: string[] }) => {
    setEditingClubId(club.id)
    setFormData({
      name: club.name,
      location: club.location || '',
      competitions: (club.competitions || []).join(', '),
    })
    setError('')
    setShowForm(true)
  }

  const parseCompetitions = (value: string) =>
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (!formData.name.trim()) {
      setError('Club name is required')
      setLoading(false)
      return
    }

    const competitionList = parseCompetitions(formData.competitions)

    try {
      if (editingClubId) {
        await updateClub(editingClubId, {
          name: formData.name.trim(),
          location: formData.location.trim(),
          competitions: competitionList,
        })
      } else {
        await addClub({
          id: crypto.randomUUID(),
          name: formData.name.trim(),
          location: formData.location.trim(),
          competitions: competitionList,
          scout_id: user?.id,
          is_verified: false,
          is_visible: true,
        } as any)
      }
      resetForm()
      fetchClubs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save club')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (clubId: string) => {
    if (!confirm('Delete this club record? This cannot be undone.')) return
    try {
      await deleteClub(clubId)
      fetchClubs()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete club')
    }
  }

  const handleVerify = async (clubId: string, isVerified: boolean) => {
    try {
      await updateClub(clubId, { is_verified: isVerified })
      fetchClubs()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update club verification')
    }
  }

  const handleVisibility = async (clubId: string, isVisible: boolean) => {
    try {
      await updateClub(clubId, { is_visible: isVisible })
      fetchClubs()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update club visibility')
    }
  }

  const getClubPlayerIds = (clubId: string, clubName: string) => {
    const club = clubs.find((entry) => entry.id === clubId)
    const fallbackClub = { id: clubId, name: clubName } as typeof clubs[number]

    return players
      .filter((player) => playerBelongsToClub(player, club || fallbackClub))
      .map((player) => player.id)
  }

  const handleBulkPublicToggle = async (clubId: string, clubName: string, isPublic: boolean) => {
    const playerIds = getClubPlayerIds(clubId, clubName)
    if (!playerIds.length) {
      alert('No players were found for this team.')
      return
    }

    setBulkUpdatingClubId(clubId)
    try {
      const result = await bulkUpdatePlayers(playerIds, { is_public: isPublic }, { useServiceRole: true })
      if (result.failedIds.length > 0) {
        alert(`${result.updated} player profiles were updated. ${result.failedIds.length} could not be updated.`)
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update team player visibility')
    } finally {
      setBulkUpdatingClubId(null)
    }
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 text-red-200">
        Access denied. Admin only.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest font-black text-gray-500">Registered Clubs</p>
            <Building2 className="text-green-500" size={18} />
          </div>
          <p className="text-3xl font-black text-white">{stats.totalClubs}</p>
        </div>
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest font-black text-gray-500">Clubs With Squad</p>
            <ShieldCheck className="text-blue-400" size={18} />
          </div>
          <p className="text-3xl font-black text-white">{stats.clubsWithSquads}</p>
        </div>
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest font-black text-gray-500">Linked Players</p>
            <Users className="text-purple-400" size={18} />
          </div>
          <p className="text-3xl font-black text-white">{stats.totalPlayersLinked}</p>
        </div>
      </div>

      <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 size={24} />
              Club Management
            </h2>
            <p className="text-sm text-gray-500 mt-1">Create, edit, and remove registered clubs from one place.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clubs..."
                className="pl-10 pr-4 py-2 bg-[#0d1117] border border-gray-800 rounded-lg text-white text-sm focus:border-green-500 outline-none w-full sm:w-72"
              />
            </div>
            <button
              onClick={openCreate}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold transition"
            >
              <Plus size={18} />
              Add Club
            </button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-800 border border-gray-700 rounded-lg space-y-4">
            {error && <div className="p-3 bg-red-900/30 border border-red-700 rounded text-red-200 text-sm">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Club Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter club name"
                  className="w-full px-4 py-2 bg-[#0d1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-green-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="City or region"
                  className="w-full px-4 py-2 bg-[#0d1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-green-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Competitions</label>
              <input
                type="text"
                value={formData.competitions}
                onChange={(e) => setFormData({ ...formData, competitions: e.target.value })}
                placeholder="e.g. NPFL, NLO, Local Cup"
                className="w-full px-4 py-2 bg-[#0d1117] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-green-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-2">Enter comma-separated competitions the club participates in.</p>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg text-white font-semibold transition"
              >
                {loading ? 'Saving...' : editingClubId ? 'Update Club' : 'Create Club'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-semibold transition"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {filteredClubs.length === 0 ? (
          <p className="text-gray-400">No clubs found.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredClubs.map((club) => (
              <div
                key={club.id}
                className="p-5 bg-gray-800 border border-gray-700 rounded-2xl hover:border-green-500/50 transition space-y-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{club.name}</h3>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                        club.is_verified
                          ? 'bg-emerald-900/10 border-emerald-800/30 text-emerald-400'
                          : 'bg-amber-900/10 border-amber-800/30 text-amber-400'
                      }`}>
                        {club.is_verified ? 'Verified Club' : 'Unverified Club'}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                        club.is_visible !== false
                          ? 'bg-sky-900/10 border-sky-800/30 text-sky-400'
                          : 'bg-gray-900 border-gray-800 text-gray-500'
                      }`}>
                        {club.is_visible !== false ? 'Visible' : 'Hidden'}
                      </span>
                    </div>
                    {club.location && (
                      <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                        <MapPin size={14} />
                        {club.location}
                      </p>
                    )}
                  </div>
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-green-900/10 border border-green-800/30 text-green-400">
                    {club.squadSize} players
                  </span>
                </div>

                {/* PREVIOUS DESIGN
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkPublicToggle(club.id, club.name, true)}
                    disabled={bulkUpdatingClubId === club.id || club.squadSize === 0}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm transition bg-emerald-900/10 border border-emerald-800/30 hover:bg-emerald-900/20 text-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Eye size={16} />
                    Make All Public
                  </button>
                  <button
                    onClick={() => handleBulkPublicToggle(club.id, club.name, false)}
                    disabled={bulkUpdatingClubId === club.id || club.squadSize === 0}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm transition bg-red-900/10 border border-red-800/30 hover:bg-red-900/20 text-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <EyeOff size={16} />
                    Make All Private
                  </button>
                  <button
                    onClick={() => handleVisibility(club.id, !(club.is_visible !== false))}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm transition ${
                      club.is_visible !== false
                        ? 'bg-sky-900/10 border border-sky-800/30 hover:bg-sky-900/20 text-sky-400'
                        : 'bg-gray-700 hover:bg-sky-900/20 hover:text-sky-400 text-gray-400'
                    }`}
                  >
                    {club.is_visible !== false ? <EyeOff size={16} /> : <Eye size={16} />}
                    {club.is_visible !== false ? 'Disable' : 'View'}
                  </button>
                  <button
                    onClick={() => handleVerify(club.id, !club.is_verified)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm transition ${
                      club.is_verified
                        ? 'bg-emerald-900/10 border border-emerald-800/30 hover:bg-emerald-900/20 text-emerald-400'
                        : 'bg-gray-700 hover:bg-emerald-900/20 hover:text-emerald-400 text-gray-400'
                    }`}
                  >
                    <BadgeCheck size={16} />
                    {club.is_verified ? 'Unverify' : 'Verify'}
                  </button>
                  <button
                    onClick={() => onViewClubDashboard?.(club.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-cyan-900/20 hover:text-cyan-400 rounded text-gray-400 text-sm transition"
                  >
                    <Monitor size={16} />
                    View Dashboard
                  </button>
                  <button
                    onClick={() => openEdit(club)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-yellow-900/20 hover:text-yellow-500 rounded text-gray-400 text-sm transition"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(club.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-red-900/20 hover:text-red-500 rounded text-gray-400 text-sm transition"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
                */}

                {/* NEW OPTIMIZED DESIGN */}
                <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap hide-scrollbar max-w-full pb-1 -mb-1">
                  <button
                    onClick={() => handleBulkPublicToggle(club.id, club.name, true)}
                    disabled={bulkUpdatingClubId === club.id || club.squadSize === 0}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-800/30 bg-emerald-900/10 hover:bg-emerald-900/20 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Eye size={14} />
                    All Public
                  </button>
                  <button
                    onClick={() => handleBulkPublicToggle(club.id, club.name, false)}
                    disabled={bulkUpdatingClubId === club.id || club.squadSize === 0}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-red-400 border border-red-800/30 bg-red-900/10 hover:bg-red-900/20 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <EyeOff size={14} />
                    All Private
                  </button>
                  <button
                    onClick={() => handleVisibility(club.id, !(club.is_visible !== false))}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition border ${
                      club.is_visible !== false
                        ? 'bg-sky-900/10 border-sky-800/30 text-sky-400 hover:bg-sky-900/20'
                        : 'bg-gray-900 border-gray-800 text-gray-400 hover:text-sky-400 hover:bg-sky-900/10'
                    }`}
                  >
                    {club.is_visible !== false ? <EyeOff size={14} /> : <Eye size={14} />}
                    {club.is_visible !== false ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={() => handleVerify(club.id, !club.is_verified)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition border ${
                      club.is_verified
                        ? 'bg-emerald-900/10 border-emerald-800/30 text-emerald-400 hover:bg-emerald-900/20'
                        : 'bg-gray-900 border-gray-800 text-gray-400 hover:text-emerald-400 hover:bg-emerald-900/10'
                    }`}
                  >
                    <BadgeCheck size={14} />
                    {club.is_verified ? 'Unverify' : 'Verify'}
                  </button>
                  <button
                    onClick={() => onViewClubDashboard?.(club.id)}
                    className="shrink-0 p-1.5 text-cyan-400 bg-cyan-900/10 border border-cyan-800/30 hover:bg-cyan-900/20 rounded-xl transition flex items-center justify-center"
                    title="View Dashboard"
                  >
                    <Monitor size={14} />
                  </button>
                  <button
                    onClick={() => openEdit(club)}
                    className="shrink-0 p-1.5 text-yellow-500 bg-yellow-900/10 border border-yellow-800/30 hover:bg-yellow-900/20 rounded-xl transition flex items-center justify-center"
                    title="Edit Club"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(club.id)}
                    className="shrink-0 p-1.5 text-red-500 bg-red-900/10 border border-red-800/30 hover:bg-red-900/20 rounded-xl transition flex items-center justify-center"
                    title="Delete Club"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminClubs
